import React, { useContext, useState, useEffect, createContext } from 'react';
import { firestore } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';

const pagesContext = createContext();
export const usePages = () => useContext(pagesContext);

const makePagesTree = (pagesDocsList) => {
  const pagesUids = pagesDocsList.reduce((acc, page, curr) => {
    acc[page.pageUid] = curr;
    return acc;
  }, {});

  let parentPage;
  const pagesTree = [];
  pagesDocsList.forEach((page) => {
    if (page.parent === null) {
      parentPage = page;
      pagesTree.push(parentPage);
      return;
    }
    const parentPages = pagesDocsList[pagesUids[page.parent]];
    parentPages.children = [...(parentPages.children || []), page];
  });

  return {
    pagesTree,
  };
};

const getAllDashboards = async (currentUser) => {
  try {
    const dashboardsDocs = await getDocs(collection(firestore, 'dashboards'));
    const dashboardsList = dashboardsDocs.docs.map((doc) => doc.data());
    const sortedDashboardsList = dashboardsList.sort((a, b) => a.dashboardNumber - b.dashboardNumber);
    const filteredDashboardsList = currentUser.claims.dashboards[0] === '*' ? sortedDashboardsList : sortedDashboardsList.filter(dashboard => currentUser.claims.dashboards.includes(dashboard.dashboardName));
    return { filteredDashboardsList };
  } catch (err) {
    console.error(err, "Error while fetching dashboards.");
    return { filteredDashboardsList: null };
  }
};

const getAllPagesForDashboard = async (dashboardUid, currentUser) => {
  try {
    const dashboardsRef = collection(firestore, `dashboards/${dashboardUid}/pages`);
    const pagesDocs = await getDocs(dashboardsRef);
    const pagesList = pagesDocs.docs.map((doc) => doc.data());
    const sortedPagesList = pagesList.sort((a, b) => a.pageNumber - b.pageNumber);
    const filteredPagesList = currentUser.claims.advertisers[0] === '*' ? sortedPagesList : sortedPagesList.filter(page => page.advertiser === undefined || page.advertiser === null || (page.advertiser !== null && currentUser.claims.advertisers.includes(page.advertiser)));
    return { pagesDocsList: filteredPagesList };
  } catch (err) {
    console.error(err, "Error while fetching dashboard pages.");
    return { pagesDocsList: null };
  }
};

const getAllPages = async (currentUser) => {
  try {
    const { filteredDashboardsList } = await getAllDashboards(currentUser);
    const allPagesDocsList = [];

    for (const dashboard of filteredDashboardsList) {
      const { pagesDocsList } = await getAllPagesForDashboard(dashboard.dashboardUid, currentUser);
      allPagesDocsList.push(...pagesDocsList);
    }

    const allPagesDocsMap = new Map(allPagesDocsList.map((page) => [page.pageUid, page]));
    return { allPagesDocsList, allPagesDocsMap };
  } catch (err) {
    console.error(err, "Error while fetching dashboard pages.");
    return { allPagesDocsList: null, allPagesDocsMap: null };
  }
};

const getPages = async (currentUser) => {
  try {
    const { allPagesDocsList, allPagesDocsMap } = await getAllPages(currentUser);
    const { pagesTree } = makePagesTree(allPagesDocsList);
    
    const defaultPage = allPagesDocsList.reduce((acc, curr) => {
      if (!acc || acc.pageNumber > curr.pageNumber) {
        return curr;
      }
      return acc;
    }, null);

    return { pagesTree, defaultPage, allPagesDocsMap, allPagesDocsList };
  } catch (err) {
    console.warn('Error while fetching the dashboard doc.');
    console.error(err);
    return { pagesTree: null, defaultPage: null, allPagesDocsMap: null, allPagesDocsList: null };
  }
};

export const useProvidePages = () => {
  const auth = useAuth();
  const [pagesData, setPagesData] = useState({
    pages: null,
    flatPages: null,
    dashPages: null,
    priming: true,
    currentPage: null,
  });

  const changeCurrentPage = (pageUid) => {
    setPagesData((prevData) => ({ ...prevData, currentPage: pageUid }));
  };

  useEffect(() => {
    const fetchPagesDocuments = async () => {
      try {
        const {
          pagesTree,
          defaultPage,
          allPagesDocsMap,
          allPagesDocsList,
        } = await getPages(auth.currentUser);

        setPagesData((prevData) => ({
          ...prevData,
          pages: pagesTree,
          dashPages: allPagesDocsMap,
          flatPages: allPagesDocsList,
          currentPage: defaultPage?.pageUid,
          priming: false,
        }));
      } catch (error) {
        console.error(error, 'Error fetching documents.');
        setPagesData((prevData) => ({ ...prevData, priming: false }));
      }
    };

    if (auth.fbUser && auth.currentUser != null) {
      fetchPagesDocuments();
    }
  }, [auth]);

  return {
    ...pagesData,
    changeCurrentPage,
  };
};

export const ProvidePages = ({ children }) => {
  const pages = useProvidePages();
  return (
    <pagesContext.Provider value={pages}>
      {children}
    </pagesContext.Provider>
  );
};

export default ProvidePages;
